import { useMemo, useState } from "react";
import { Layers, AlertTriangle, Sparkles, Trophy, TrendingDown, Minus, Check, BadgeCheck, Loader2, Crown, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatOdds, PARLAY_DISCLAIMER } from "@/lib/parlay";
import UpgradeModal from "@/components/UpgradeModal";
import { startParlayCheckout } from "@/lib/billing";
import { toast } from "sonner";

interface Leg {
  id: string;
  leg_order: number;
  game_description: string;
  pick_description: string;
  line: number | null;
  odds: number | null;
  reasoning: string | null;
  sports?: { code: string } | null;
}

interface Props {
  parlay: {
    id: string;
    title: string;
    total_odds: number | null;
    suggested_units: number;
    risk: string;
    ai_summary: string | null;
    correlation_notes: string | null;
    leg_count: number;
    betting_date?: string | null;
    result?: string | null;
    units_delta?: number | null;
  };
  legs: Leg[] | null;
  locked: boolean;
  canBuyParlay: boolean;
  /** Free user already purchased today's parlay */
  parlayPurchased?: boolean;
  /** Pro or admin */
  isPremium?: boolean;
}

function ResultChip({ result, units }: { result?: string | null; units?: number | null }) {
  if (!result || result === "pending" || result === "void") return null;
  const u = Number(units ?? 0);
  if (result === "won") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-success/40 bg-success/15 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-success">
        <Trophy className="w-3 h-3" /> Won{u ? ` +${u.toFixed(2)}u` : ""}
      </span>
    );
  }
  if (result === "lost") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-danger/40 bg-danger/15 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-danger">
        <TrendingDown className="w-3 h-3" /> Lost {u.toFixed(2)}u
      </span>
    );
  }
  if (result === "pushed") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-muted-foreground/30 bg-muted/30 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
        <Minus className="w-3 h-3" /> Push
      </span>
    );
  }
  return null;
}

export default function ParlayOfDayCard({ parlay, legs, locked, canBuyParlay, parlayPurchased, isPremium }: Props) {
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [buying, setBuying] = useState(false);

  const sportCodes = useMemo(() => {
    const codes = (legs ?? []).map((l) => l.sports?.code).filter(Boolean) as string[];
    return Array.from(new Set(codes));
  }, [legs]);
  const isMultiSport = sportCodes.length > 1;

  const handleBuyParlay = async () => {
    if (buying) return;
    setBuying(true);
    const tid = toast.loading("Opening secure checkout…");
    try {
      await startParlayCheckout(parlay.id);
      toast.success("Redirecting to checkout…", { id: tid });
    } catch (e: any) {
      toast.error(e?.message ?? "Unable to start parlay checkout", { id: tid });
    } finally {
      setBuying(false);
    }
  };

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl border border-accent/30 bg-gradient-to-br from-accent/[0.08] via-card to-card shadow-[0_10px_40px_-12px_hsl(35_95%_58%/0.25)]">
        <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-accent via-primary to-accent" />
        <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-accent/10 blur-3xl" />

        {/* Header */}
        <div className="relative flex flex-wrap items-center justify-between gap-2 border-b border-accent/20 bg-accent/[0.04] px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2 font-display text-[11px] font-bold uppercase tracking-[0.18em] text-accent">
            <Layers className="h-4 w-4" />
            Parlay of the Day
            {parlay.betting_date && (
              <span className="ml-1 text-muted-foreground/80 font-semibold tracking-wider">
                · {new Date(parlay.betting_date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <ResultChip result={parlay.result} units={parlay.units_delta} />
            {!locked && parlayPurchased && !isPremium && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-success/30 bg-success/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-success">
                <BadgeCheck className="w-3 h-3" /> Parlay Access Purchased
              </span>
            )}
            {isPremium && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <Crown className="w-3 h-3" /> Pro
              </span>
            )}
            <span className="te-chip border border-accent/30 bg-accent/10 text-accent capitalize text-[11px]">
              {parlay.risk} variance
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="relative p-4 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0 flex-1">
              <h3 className="font-display text-xl sm:text-2xl font-bold leading-tight break-words">
                {locked ? `${parlay.leg_count}-Leg Parlay` : parlay.title}
              </h3>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {isMultiSport ? (
                  <span className="te-chip border border-primary/30 bg-primary/10 text-primary text-[10px] uppercase tracking-wider">
                    Multi-sport
                  </span>
                ) : null}
                {sportCodes.map((c) => (
                  <span
                    key={c}
                    className="te-chip border border-border bg-secondary/40 text-[10px] uppercase tracking-wider text-muted-foreground"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 sm:flex sm:items-center sm:gap-4 sm:text-right shrink-0">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Odds</div>
                <div className="font-display text-lg sm:text-xl font-bold te-grad-text">
                  {locked ? "—" : formatOdds(parlay.total_odds)}
                </div>
              </div>
              <div className="hidden sm:block h-8 w-px bg-border" />
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Units</div>
                <div className="font-display text-lg sm:text-xl font-bold">
                  {locked ? "—" : `${parlay.suggested_units}u`}
                </div>
              </div>
              <div className="hidden sm:block h-8 w-px bg-border" />
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Legs</div>
                <div className="font-display text-lg sm:text-xl font-bold">{parlay.leg_count}</div>
              </div>
            </div>
          </div>

          {!locked && parlay.ai_summary && (
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{parlay.ai_summary}</p>
          )}

          {locked ? (
            <div className="mt-5">
              <div className="mb-4 text-center">
                <div className="font-display text-lg font-semibold">Two ways to access today's parlay</div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Get the full {parlay.leg_count}-leg breakdown, total odds, and analysis.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {/* One-time: Parlay only */}
                {canBuyParlay && (
                  <div className="relative flex flex-col rounded-xl border border-accent/40 bg-gradient-to-b from-accent/[0.08] to-card p-4 sm:p-5">
                    <div className="mb-3 flex items-center justify-between">
                      <div className="font-display text-sm font-bold uppercase tracking-wider text-accent">
                        Today's Parlay
                      </div>
                      <span className="rounded-full border border-accent/30 bg-accent/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent">
                        One-time
                      </span>
                    </div>
                    <div className="mb-3 flex items-baseline gap-1">
                      <span className="font-display text-3xl font-bold">$2.99</span>
                      <span className="text-xs text-muted-foreground">/ today</span>
                    </div>
                    <ul className="mb-4 space-y-1.5 text-xs text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" />
                        <span>Today's parlay only</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" />
                        <span>All {parlay.leg_count} legs with reasoning</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Calendar className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" />
                        <span>Expires at end of day</span>
                      </li>
                    </ul>
                    <Button
                      onClick={handleBuyParlay}
                      disabled={buying}
                      className="mt-auto w-full bg-gradient-accent text-accent-foreground hover:opacity-90"
                      size="sm"
                    >
                      {buying ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Starting…</>
                      ) : (
                        <><Sparkles className="mr-2 h-4 w-4" /> Unlock for $2.99</>
                      )}
                    </Button>
                  </div>
                )}

                {/* Pro */}
                <div className="relative flex flex-col rounded-xl border border-primary/40 bg-gradient-to-b from-primary/[0.08] to-card p-4 sm:p-5">
                  <div className="absolute -top-2 right-3 rounded-full border border-primary/40 bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-foreground shadow-sm">
                    Best value
                  </div>
                  <div className="mb-3 flex items-center justify-between">
                    <div className="font-display text-sm font-bold uppercase tracking-wider text-primary flex items-center gap-1.5">
                      <Crown className="h-3.5 w-3.5" /> Go Pro
                    </div>
                    <span className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                      Monthly
                    </span>
                  </div>
                  <div className="mb-3 flex items-baseline gap-1">
                    <span className="font-display text-3xl font-bold">$19.99</span>
                    <span className="text-xs text-muted-foreground">/ month</span>
                  </div>
                  <ul className="mb-4 space-y-1.5 text-xs text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                      <span>All Top 5 picks, every day</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                      <span>Parlay of the Day included</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                      <span>Full analysis & reasoning</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                      <span>Premium history & insights</span>
                    </li>
                  </ul>
                  <Button
                    onClick={() => setUpgradeOpen(true)}
                    variant="outline"
                    size="sm"
                    className="mt-auto w-full border-primary/40 hover:bg-primary/10"
                  >
                    <Crown className="mr-2 h-4 w-4" /> Go Pro — $19.99/mo
                  </Button>
                </div>
              </div>

              <p className="mt-3 text-center text-[11px] text-muted-foreground">
                Cancel anytime · Secure checkout via Stripe
              </p>
            </div>
          ) : (
            <div className="mt-5 space-y-2.5">
              {(legs ?? [])
                .sort((a, b) => a.leg_order - b.leg_order)
                .map((leg, i) => (
                  <div key={leg.id} className="rounded-xl border border-border/60 bg-secondary/30 p-3 sm:p-4">
                    <div className="mb-1.5 flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent/20 text-[10px] font-bold text-accent">
                          {i + 1}
                        </span>
                        {leg.sports?.code && (
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {leg.sports.code}
                          </span>
                        )}
                      </div>
                      <span className="te-chip shrink-0 border border-border bg-background font-mono text-xs">
                        {formatOdds(leg.odds)}
                      </span>
                    </div>
                    <div className="font-semibold text-sm break-words">{leg.pick_description}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground break-words">{leg.game_description}</div>
                    {leg.reasoning && (
                      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{leg.reasoning}</p>
                    )}
                  </div>
                ))}
              {parlay.correlation_notes && (
                <div className="rounded-xl border border-border/60 bg-background p-3 text-sm">
                  <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">Correlation notes</div>
                  {parlay.correlation_notes}
                </div>
              )}

              {parlayPurchased && !isPremium && (
                <div className="flex items-start gap-2 rounded-xl border border-primary/30 bg-primary/[0.06] p-3 text-xs">
                  <Crown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                  <div className="flex-1">
                    <div className="font-semibold text-foreground">Want every Top 5 pick too?</div>
                    <div className="text-muted-foreground">Pro includes the parlay daily plus full Top 5 access for $19.99/mo.</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setUpgradeOpen(true)} className="border-primary/40 hover:bg-primary/10">
                    Go Pro
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="mt-5 flex items-start gap-2 border-t border-border/60 pt-3 text-xs text-muted-foreground">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>High variance. Entertainment-first. {PARLAY_DISCLAIMER}</span>
          </div>
        </div>
      </div>
      <UpgradeModal open={upgradeOpen} onOpenChange={setUpgradeOpen} />
    </>
  );
}
